import { useQuery } from "@tanstack/react-query";

import { apiFetchJson } from "@/utils/apiFetch";
import type { SiteTrialMetricRow } from "@/views/TrialPlanning/shortlist";

const PAGE_SIZE = 1000;
/** Guard against an unbounded loop if the endpoint ever misreports `count`. */
const MAX_PAGES = 30;

interface MetricsPage {
  results: SiteTrialMetricRow[];
  count: number;
}

/**
 * Raw per-(site, trial) performance metrics for the whole portfolio, which the
 * shortlist scores its site pillars from.
 *
 * Fetched per site *per trial* — not pre-aggregated — because the method
 * compares a site against the peers who ran the same protocol before
 * averaging, and weights each trial by how well it matches the target
 * profile. `source_system=all` so every feed we hold contributes, not just
 * one vendor's. Absent or failing, the shortlist still scores investigator
 * pillars.
 */
export function useSiteTrialMetrics(): {
  rows: SiteTrialMetricRow[] | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ["site-trial-metrics"],
    queryFn: async (): Promise<SiteTrialMetricRow[] | null> => {
      const url = (offset: number): string =>
        `${import.meta.env.VITE_API_URL}/data/portfolio/site-trial-metrics/?source_system=all&limit=${PAGE_SIZE}&offset=${offset}`;
      try {
        const first = await apiFetchJson<MetricsPage>(url(0));
        const rows = [...(first?.results ?? [])];
        const total = first?.count ?? rows.length;
        for (
          let offset = PAGE_SIZE, page = 0;
          offset < total && page < MAX_PAGES;
          offset += PAGE_SIZE, page++
        ) {
          const next = await apiFetchJson<MetricsPage>(url(offset));
          if (!next?.results?.length) break;
          rows.push(...next.results);
        }
        return rows;
      } catch {
        return null;
      }
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  return { rows: data ?? undefined, isLoading };
}
